import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import CloseIcon from '@material-ui/icons/Close';
import ShareIcon from '@material-ui/icons/Share';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import Button from 'UI/button';
import clsx from 'clsx';
import itinerarySentGif from 'assets/gifs/itinerary-sent.gif';
import axios from 'axios';
import * as _ from 'lodash';
import { baseURL } from 'utils/apiEndpoints';
import { TIMEOUT } from 'utils/consts';
import { getUpiPaymentLink } from 'utils/common';
import moment from 'moment';

class ItinerarySentModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      copied: false,
      isDownloading: false,
      downloadText: `DOWNLOAD PDF${'\u00A0'.repeat(3)}`,
    };
    this.dotsIndex = 0;
  }

  handleClose = () => {
    const { closePricingModal } = this.props;
    closePricingModal();
  };

  handleCopyLink = () => {
    const { handleGetLink } = this.props;
    handleGetLink();
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  handleDownloadPDF = async () => {
    const { handleGetPDF } = this.props;
    this.setState({ isDownloading: true });
    this.startLoadingAnimation();
    
    try {
      await handleGetPDF();
    } finally {
      this.setState({ isDownloading: false });
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.dotsIndex = 0;
        this.setState({ downloadText: `DOWNLOAD PDF${'\u00A0'.repeat(3)}` });
      }
    }
  };

  startLoadingAnimation = () => {
    this.loadingInterval = setInterval(() => {
      this.dotsIndex = (this.dotsIndex + 1) % 4;
      const dots = '.'.repeat(this.dotsIndex);
      const padding = '\u00A0'.repeat(3 - this.dotsIndex);
      this.setState({ downloadText: `DOWNLOAD PDF${dots}${padding}` });
    }, 500);
  };

  handleGetUPILink = async () => {
    const { aiItineraryData, itineraryId, showSnackbar } = this.props;
    let { paymentLink } = this.state;

    try {    
      if (_.isNil(paymentLink)) {
        this.setState({ fetchingUpiPaymentLink: true });
        paymentLink = await getUpiPaymentLink(itineraryId, aiItineraryData.tokenAmount);
      }
      if (!navigator.clipboard) { throw new Error('Clipboard API not available'); }
      await navigator.clipboard.writeText(paymentLink);       
      this.setState({ fetchingUpiPaymentLink: false, paymentLink });
      showSnackbar('UPI Link Copied', 'success');
    } catch (error) {
      this.setState({ fetchingUpiPaymentLink: false });
      showSnackbar('Something went wrong, please contact support', 'error');
    }
  };

  handleResendItinerary = async () => {
    const {
      aiItineraryData, itineraryId, showSnackbar,
      closePricingModal,
    } = this.props;
    await this.setState({ resendingItinerary: true });

    try {
      await axios({
        url: `${baseURL}itineraries/${itineraryId}/resend`,
        method: 'put',        
        timeout: TIMEOUT,
      });
      this.setState({ resendingItinerary: false });
      showSnackbar('Itinerary resent successfully', 'success');
      closePricingModal();
    } catch (error) {
      this.setState({ resendingItinerary: false });
      showSnackbar('Something went wrong, please contact support', 'error');
    }
  };

  render() {
    const { classes, isOpen, aiItineraryData } = this.props;
    const {
      copied, isDownloading, downloadText, resendingItinerary,
      fetchingUpiPaymentLink,
    } = this.state;

    return (
      <Dialog
        open={isOpen}
        onClose={this.handleClose}
        maxWidth="sm"
        fullWidth
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <div className={classes.modalContainer}>
          {/* Header */}
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <ShareIcon className={classes.shareIcon} />
              <span className={classes.title}>Itinerary Sent</span>
            </div>
            <CloseIcon 
              className={classes.closeIcon} 
              onClick={this.handleClose}
            />
          </div>

          {/* Content */}
          <div className={classes.content}>
            {/* Confirmation Message */}
            <div className={classes.confirmationMessage}>
              Itinerary successfully sent on email & WhatsApp at <br />{moment(aiItineraryData.itinerarySyncedAt).format('h:mm A, Do MMM YYYY')}.
            </div>

            {
              moment(aiItineraryData.itinerarySyncedAt).isAfter(moment().subtract(5, 'minutes'))
                ? <img src={itinerarySentGif} alt="Itinerary Sent" className={classes.gifImage} />
                : (
                  <Button
                    variant="outlined"
                    className={classes.resendButton}
                    onClick={this.handleResendItinerary}
                    disabled={resendingItinerary}
                  >                 
                    {resendingItinerary ? 'Resending...' : 'Resend Itinerary'}
                  </Button>
                )
            }

            {/* Itinerary Link */}
            <div className={classes.linkContainer}>
              <div className={classes.linkWrapper}>
                <a
                  href={aiItineraryData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.linkText}
                >
                  {aiItineraryData.url.substring(0, 40)}...
                </a>
                <FileCopyOutlinedIcon 
                  className={classes.copyIcon}
                  onClick={this.handleCopyLink}
                />
              </div>
              <div className={clsx(classes.copiedMessage, copied ? null : classes.hidden)}>Copied!</div>
            </div>

            {/* Action Buttons */}
            <div className={classes.buttonContainer}>
              <Button
                variant="outlined"
                className={classes.actionButton}
                onClick={this.handleDownloadPDF}
                disabled={isDownloading}
              >
                <GetAppOutlinedIcon style={{ fontSize: 18, marginRight: 5, marginTop: 2 }} />
                {downloadText}
              </Button>
              <Button
                variant="outlined"
                className={classes.actionButton}
                disabled={fetchingUpiPaymentLink}
                onClick={this.handleGetUPILink}
              >                
                {
                  fetchingUpiPaymentLink
                    ? 'FETCHING...'
                    : (
                      <>
                        <FileCopyOutlinedIcon style={{ fontSize: 18, marginRight: 5, marginTop: 2 }} />
                        COPY UPI LINK
                      </>
                    )                  
                }         
              </Button>
            </div>

            {/* Pro Tip */}
            <div className={classes.proTipContainer}>
              <span className={classes.proTipLabel}>Pro tip:</span>
              <span className={classes.proTipText}>
                Share the itinerary PDF or link and UPI payment link with the customer personally on WhatsApp.
              </span>
            </div>
          </div>
        </div>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  dialogPaper: {
    borderRadius: 12,
    maxWidth: 700,
    margin: 16,
  },
  modalContainer: {
    background: theme.colors.white,
    borderRadius: 12,
    fontFamily: 'Sofia-pro, sans-serif',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  titleContainer: {    
    display: 'flex',
    alignItems: 'center',
  },
  shareIcon: {
    fontSize: 18,
    marginTop: 2,
    color: theme.colors.primary,
    marginRight: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: '#32302F',
    fontFamily: 'Sofia-pro, sans-serif',
  },
  closeIcon: {
    fontSize: 24,
    color: '#666666',
    cursor: 'pointer',
    '&:hover': {
      color: '#333333',
    },
  },
  content: {
    padding: '20px 60px',
    textAlign: 'center',
  },
  hidden: {
    visibility: 'hidden',
  },
  confirmationMessage: {
    fontSize: 16,
    color: theme.colors.textDark,
    marginBottom: 20,
    fontWeight: 'bold',
  },
  gifImage: {
    width: '100%',
    maxWidth: 200,
    height: 'auto',
    margin: '20px 0',
    borderRadius: 8,
  },
  linkContainer: {
    marginBottom: 24,
  },
  linkWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',        
  },
  linkText: {
    fontSize: 16,
    color: theme.colors.primary,
    textDecoration: 'none',
    marginRight: 8,
    fontFamily: 'Sofia-pro, sans-serif',
    wordBreak: 'break-all',
  },
  copyIcon: {
    fontSize: 18,
    color: '#0090B5',
    cursor: 'pointer',
    '&:hover': {
      color: '#007A9B',
    },
  },
  copiedMessage: {
    fontSize: 12,
    color: theme.colors.green,
    fontFamily: 'Sofia-pro, sans-serif',
  },
  buttonContainer: {
    display: 'flex',
    gap: '20px',
    marginBottom: 24,
    justifyContent: 'center',
  },
  actionButton: {
    width: 250,
    border: '1px solid #0090B5',
    borderRadius: 20,
    padding: '12px 20px',
    fontSize: 14,
    fontWeight: 500,
    color: '#0090B5',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: 'Sofia-pro, sans-serif',
    '&:hover': {
      backgroundColor: '#F0F8FF',
    },
  },
  proTipContainer: {
    textAlign: 'left',
    padding: '16px',
  },
  proTipLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#32302F',
    marginRight: 4,
    fontFamily: 'Sofia-pro, sans-serif',
  },
  proTipText: {
    fontSize: 14,
    color: '#666666',
    fontFamily: 'Sofia-pro, sans-serif',
  },
  resendButton: {
    width: 250,
    backgroundColor: '#0090B5',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 24,
    height: 36,
    padding: '0 40px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Sofia-pro, sans-serif',
    marginLeft: 'auto',
    marginBottom: 50,
    '&:hover': {
      backgroundColor: '#007A94',
    },
    '&:disabled': {
      backgroundColor: '#DDDDDD',
      cursor: 'not-allowed',
    },
  },
});

ItinerarySentModal.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closePricingModal: PropTypes.func.isRequired,
  aiItineraryData: PropTypes.object.isRequired,  
  showSnackbar: PropTypes.func.isRequired,
  itineraryId: PropTypes.string.isRequired,
  updateItineraryData: PropTypes.func.isRequired,
  openPricingModal: PropTypes.func.isRequired,
  handleGetPDF: PropTypes.func.isRequired,
  handleGetLink: PropTypes.func.isRequired,
};

export default withStyles(styles)(ItinerarySentModal);
